import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { PaystackButton } from 'react-paystack';
import { API_URL } from '../config';
import { FaArrowLeft } from 'react-icons/fa';
import axios from 'axios';
import { toast } from 'react-toastify';

const PaystackProductPaymentScreen = () => {
  const navigate = useNavigate();
  const [authToken, setAuthToken] = useState(null);
  const [currency, setCurrency] = useState('USD');
  const [email, setEmail] = useState('');
  const [publicKey, setPublicKey] = useState('');
  const [orderData, setOrderData] = useState(null);
  const [reference, setReference] = useState('');
  const [loading, setLoading] = useState(false);

  // 🔹 Load cart + form data
  useEffect(() => {
    const savedOrder = JSON.parse(localStorage.getItem('orderData')) || null;
    setOrderData(savedOrder);

    // Generate unique reference if not already stored
    let ref = localStorage.getItem('paystack_ref');
    if (!ref) {
      ref = `REF_${Date.now()}_${Math.floor(Math.random() * 1000)}`;
      localStorage.setItem('paystack_ref', ref);
    }
    setReference(ref);
  }, []);

  // 🔹 Fetch user + currency
  useEffect(() => {
    const retrieveAuthToken = () => {
      try {
        const tokenString = localStorage.getItem('authToken');
        if (tokenString) {
          const token = JSON.parse(tokenString);
          setAuthToken(token);
          fetchUserProfile(token);
        }
      } catch (error) {
        console.error('Error retrieving token:', error);
      }
    };

    const fetchUserProfile = async (token) => {
      try {
        const response = await fetch(`${API_URL}/user/country/`, {
          headers: { Authorization: `Bearer ${token.access}` },
        });

        if (response.ok) {
          const data = await response.json();
          setCurrency(data.country.currency);
          setEmail(data.user.email);
          setPublicKey(data.publickey);
        } else {
          console.error('Failed to fetch user profile.');
        }
      } catch (error) {
        console.error('Error fetching user profile:', error);
      }
    };

    retrieveAuthToken();
  }, []);

  // 🔹 Create order after successful payment
  const CreateOrder = useCallback(async () => {
    try {
      if (!orderData) {
        toast.info("No order data found.");
        return;
      }

      setLoading(true);

      const response = await axios.post(
        `${API_URL}/orders/`,
        {
          reference,
          cart: orderData.cart,
          total_price: orderData.totalPrice,
          address: orderData.address,
          location: orderData.location,
          phone1: orderData.phone1,
          phone2: orderData.phone2,
        },
        { headers: { Authorization: `Bearer ${authToken?.access}` } }
      );

      if (response.status === 201) {
       toast.success("Order created successfully!");
        localStorage.removeItem('cart');
        localStorage.removeItem('orderData');
        localStorage.removeItem('paystack_ref');
        navigate('/cart');
      } else {
        toast.error("Failed to create order.");
      }
    } catch (error) {
      console.error('Error creating order:', error);
      localStorage.removeItem('cart');
      localStorage.removeItem('orderData');
      localStorage.removeItem('paystack_ref');
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [orderData, reference, authToken, navigate]);

  if (!orderData) {
    return <div style={{ textAlign: 'center', marginTop: '50px' }}>No order data found.</div>;
  }

  const componentProps = {
    email,
    amount: orderData.totalPrice * 100,
    currency,
    publicKey,
    reference,
    text: 'Make Payment',
    onSuccess: async () => {
      await CreateOrder();
    },
    onClose: () => {
      if (!loading) {
        toast.error('Payment Cancelled');
        localStorage.removeItem('paystack_ref');
        navigate('/cart');
      }
    },
  };

  return (
    <div style={styles.container}>
      <button onClick={() => navigate(-1)} className="back-button" disabled={loading}>
        <FaArrowLeft className="white-arrow" />
      </button>
      {currency ? (
        <div style={styles.card}>
          <h3 style={{ marginBottom: '20px' }}>Payment Details</h3>

          <div style={{ marginBottom: '10px' }}>
            <strong>Amount:</strong> {currency}{orderData.totalPrice}
          </div>
          <div style={{ marginBottom: '20px' }}>
            <strong>Reference:</strong> {reference}
          </div>

          {loading ? (
            <div style={styles.spinner}></div>
          ) : (
            <PaystackButton {...componentProps} style={styles.paystackButton} disabled={loading} />
          )}

          <div style={{ marginTop: '20px' }}>
            <button 
              onClick={() => navigate('/cart')} 
              style={styles.cancelButton} 
              disabled={loading}
            >
              Cancel Payment
            </button>
          </div>
        </div>
      ) : (
        <div style={{ textAlign: 'center' }}>Loading...</div>
      )}
    </div>
  );
};

const styles = {
  container: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100vh',
    backgroundColor: '#f5f5f5',
  },
  card: {
    backgroundColor: '#fff',
    padding: '30px',
    borderRadius: '12px',
    boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
    width: '350px',
    textAlign: 'center',
    fontFamily: 'Arial, sans-serif',
  },
  paystackButton: {
    backgroundColor: '#32CD32',
    color: '#fff',
    border: 'none',
    padding: '12px 20px',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '16px',
    width: '100%',
    marginBottom: '15px',
  },
  cancelButton: {
    backgroundColor: '#FF6347',
    color: '#fff',
    border: 'none',
    padding: '10px 20px',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '16px',
    width: '100%',
  },
  spinner: {
    border: '4px solid #f3f3f3',
    borderTop: '4px solid #32CD32',
    borderRadius: '50%',
    width: '36px',
    height: '36px',
    margin: '0 auto 15px auto',
    animation: 'spin 1s linear infinite',
  },
};

// CSS animation
const styleSheet = document.styleSheets[0];
styleSheet.insertRule(`
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`, styleSheet.cssRules.length);

export default PaystackProductPaymentScreen;
